/**
 * ConfirmCarrierBookingModal — intercepts the pickup_scheduled stage transition.
 *
 * When ops moves a pickup to "Pickup Scheduled", this modal collects the
 * carrier booking details (carrier name, BOL/ref#, dates) before creating a
 * freight tracking row and advancing the stage. The PO must be linked first.
 *
 * On confirm: POST /api/freight/register-external → creates freight row →
 *             writes freight_ref / freight_carrier_name / freight_bol to Odoo PO →
 *             then transitions the pickup to pickup_scheduled.
 *
 * "Skip" path: bypasses freight registration and just transitions the stage.
 */

import { useState } from 'react'
import { Button } from '@/components/ui'
import type { RegisterExternalFreightBody } from '../api/queries'
import type { PickupDetail } from '../api/types'

interface Props {
  pickup: PickupDetail
  onConfirm: (body: RegisterExternalFreightBody) => Promise<void>
  onSkip: () => void
  onCancel: () => void
  isPending: boolean
}

const KNOWN_PROVIDERS: { key: string; label: string }[] = [
  { key: 'tql', label: 'TQL Logistics' },
  { key: 'echo_gl', label: 'Echo Global Logistics' },
  { key: 'wwex', label: 'WWEX / Unishippers' },
  { key: 'manual', label: 'Other carrier (UPS, FedEx, LTL…)' },
]

export function ConfirmCarrierBookingModal({ pickup, onConfirm, onSkip, onCancel, isPending }: Props) {
  const [carrierName, setCarrierName] = useState(pickup.carrier_name ?? '')
  const [provider, setProvider] = useState('manual')
  const [providerRef, setProviderRef] = useState('')
  const [bolNumber, setBolNumber] = useState('')
  const [pickupDate, setPickupDate] = useState(pickup.proposed_pickup_date ?? '')
  const [estDelivery, setEstDelivery] = useState('')
  const [poName, setPoName] = useState(pickup.odoo_po_names[0] ?? '')
  const [poError, setPoError] = useState('')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!poName.trim()) {
      setPoError('PO number is required before scheduling a pickup.')
      return
    }
    if (!carrierName.trim() || !providerRef.trim()) return

    const body: RegisterExternalFreightBody = {
      odoo_po_name: poName.trim(),
      carrier_name: carrierName.trim(),
      provider,
      provider_ref: providerRef.trim(),
      material_pickup_id: pickup.id,
    }
    if (bolNumber.trim()) body.bol_number = bolNumber.trim()
    if (pickupDate) body.pickup_date = pickupDate
    if (estDelivery) body.est_delivery = estDelivery

    void onConfirm(body)
  }

  return (
    /* Full-screen dim overlay */
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl border border-border-subtle bg-surface shadow-xl">
        <div className="border-b border-border-subtle px-6 py-4">
          <h2 className="text-role-page-title m-0">Confirm carrier booking</h2>
          <p className="text-role-page-subtitle mt-1 m-0">
            Register the shipment with SPI Freight — the carrier ref and BOL will
            be written to the Odoo PO.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-4">
          {/* PO (required) */}
          <div>
            <label className="text-role-metadata block mb-1" htmlFor="cb-po">
              Odoo PO <span className="text-danger-text">*</span>
            </label>
            <input
              id="cb-po"
              type="text"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
              value={poName}
              onChange={(e) => { setPoName(e.target.value); setPoError('') }}
              placeholder="e.g. P01756"
              required
            />
            {poError ? <p className="mt-1 text-xs text-danger-text">{poError}</p> : null}
          </div>

          {/* Carrier name */}
          <div>
            <label className="text-role-metadata block mb-1" htmlFor="cb-carrier">
              Carrier name <span className="text-danger-text">*</span>
            </label>
            <input
              id="cb-carrier"
              type="text"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
              value={carrierName}
              onChange={(e) => setCarrierName(e.target.value)}
              placeholder="e.g. Estes Express"
              required
            />
          </div>

          {/* Provider */}
          <div>
            <label className="text-role-metadata block mb-1" htmlFor="cb-provider">
              Provider (SPI registry)
            </label>
            <select
              id="cb-provider"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              {KNOWN_PROVIDERS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Reference # */}
          <div>
            <label className="text-role-metadata block mb-1" htmlFor="cb-ref">
              Tracking ref / PRO / carrier # <span className="text-danger-text">*</span>
            </label>
            <input
              id="cb-ref"
              type="text"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
              value={providerRef}
              onChange={(e) => setProviderRef(e.target.value)}
              placeholder="BOL, PRO, or tracking reference"
              required
            />
          </div>

          {/* BOL (optional) */}
          <div>
            <label className="text-role-metadata block mb-1" htmlFor="cb-bol">
              BOL number <span className="text-tertiary">(if different)</span>
            </label>
            <input
              id="cb-bol"
              type="text"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
              value={bolNumber}
              onChange={(e) => setBolNumber(e.target.value)}
              placeholder="Optional"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-role-metadata block mb-1" htmlFor="cb-pickup-date">
                Pickup date
              </label>
              <input
                id="cb-pickup-date"
                type="date"
                className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-role-metadata block mb-1" htmlFor="cb-est-delivery">
                Est. delivery
              </label>
              <input
                id="cb-est-delivery"
                type="date"
                className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
                value={estDelivery}
                onChange={(e) => setEstDelivery(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              className="text-sm text-tertiary underline underline-offset-2 hover:text-secondary"
              onClick={onSkip}
              disabled={isPending}
            >
              Skip — schedule without tracking
            </button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={isPending || !carrierName.trim() || !providerRef.trim() || !poName.trim()}
              >
                {isPending ? 'Registering…' : 'Register & schedule'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
